import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, useLocation } from 'react-router-dom';
import { Settings, User, LogOut, ShieldAlert, ArrowLeft } from 'lucide-react';
import DashboardTabs from './components/admin/DashboardTabs';
import AdminLanding from './components/admin/AdminLanding';
import UomAdmin from './components/admin/UomAdmin';

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    async function fetchUser() {
      try {
        const response = await fetch('/.auth/me');
        const data = await response.json();
        if (data.clientPrincipal) {
          setUser(data.clientPrincipal);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
      // Not authenticated — redirect to AAD login
      window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(window.location.href)}`;
    }
    fetchUser();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
      </div>
    );
  }

  const isAdmin = user?.userRoles?.includes('po_processor_admins');

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <div className="bg-slate-800 rounded-xl p-8 max-w-md text-center border border-slate-700">
          <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-slate-400 mb-6">
            You don't have permission to access admin tools. Contact your administrator to request the po_processor_admins role.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to PO Processor
          </Link>
        </div>
      </div>
    );
  }

  // Show "Admin Home" breadcrumb on sub-pages
  const isSubPage = location.pathname !== '/admin' && location.pathname !== '/admin/';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Admin Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">PO Processor Admin</h1>
            <p className="text-xs text-slate-500">Configuration & Analytics</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {isSubPage && (
            <Link
              to="/admin"
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Admin Home
            </Link>
          )}
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          >
            PO Processor
          </Link>
          {user && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-slate-300">
                <div className="w-7 h-7 bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <span className="text-sm">{user.userDetails}</span>
              </div>
              <a
                href="/.auth/logout?post_logout_redirect_uri=/"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </a>
            </div>
          )}
        </div>
      </header>

      {/* Content — sub-routed */}
      <main className="flex-1 p-6">
        <Routes>
          <Route index element={<AdminLanding />} />
          <Route path="dashboards" element={<DashboardTabs />} />
          <Route path="uom" element={<UomAdmin />} />
        </Routes>
      </main>
    </div>
  );
}
